import * as React from "react";
import { cn } from "../../lib/utils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "./table";
import { Badge } from "./badge";
import { StatusBadge } from "./status-badge";
import { Button } from "./button";
import { Switch } from "./switch";
import { Skeleton } from "./skeleton";
import { MobileSupplierCard } from "./mobile-supplier-card";
import { ResponsiveTableContainer } from "./responsive-table-container";
import {
  MapPin,
  Edit,
  Trash2,
  CheckCircle,
  Clock,
  Loader2,
  Users,
  Plus
} from "lucide-react";
import { Supplier } from "../../lib/api";

interface ResponsiveSupplierListProps {
  suppliers: Supplier[];
  isLoading?: boolean;
  onView: (id: string) => void;
  onEdit: (supplier: Supplier) => void;
  onDelete: (id: string) => void;
  onToggleActive: (id: string) => void;
  onAddProduct: (supplier: Supplier) => void;
  isDeleting?: string | null;
  isToggling?: string | null;
  className?: string;
}

const ResponsiveSupplierList = React.forwardRef<
  HTMLDivElement,
  ResponsiveSupplierListProps
>(({
  suppliers,
  isLoading = false,
  onView,
  onEdit,
  onDelete,
  onToggleActive,
  onAddProduct,
  isDeleting = null,
  isToggling = null,
  className,
  ...props
}, ref) => {

  // Helper function to get product names
  const getProductNames = (supplier: Supplier) => {
    return supplier.products && supplier.products.length > 0
      ? `${supplier.products.length} products`
      : "No products";
  };

  // Helper function to check if supplier is verified
  const isVerified = (supplier: Supplier) => {
    return supplier.verificationStatus === 'verified';
  };

  return (
    <div ref={ref} className={cn("w-full", className)} {...props}>
      {/* Desktop/Tablet Table View - hidden on mobile */}
      <div className="hidden md:block">
        <ResponsiveTableContainer maxHeight="none">
          <Table>
            <TableHeader className="sticky top-0 bg-background z-10">
              <TableRow>
                <TableHead>Business Name</TableHead>
                <TableHead>Owner</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Products</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Verified</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                // Loading skeleton rows
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <Skeleton className="h-4 w-40" />
                        <Skeleton className="h-3 w-24" />
                      </div>
                    </TableCell>
                    <TableCell><Skeleton className="h-4 w-28" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-36" /></TableCell>
                    <TableCell><Skeleton className="h-6 w-20" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                    <TableCell>
                      <div className="flex space-x-2 justify-end">
                        <Skeleton className="h-8 w-8" />
                        <Skeleton className="h-8 w-8" />
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : suppliers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    <div className="flex flex-col items-center space-y-2">
                      <Users className="h-12 w-12 text-muted-foreground" />
                      <p className="text-lg font-medium">No suppliers found</p>
                      <p className="text-sm text-muted-foreground">Add your first supplier to get started</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                suppliers.map((supplier) => (
                  <TableRow
                    key={supplier._id}
                    className="hover:bg-muted/50 cursor-pointer"
                    onClick={() => onView(supplier._id)}
                  >
                    <TableCell className="font-medium">{supplier.businessName}</TableCell>
                    <TableCell>{supplier.ownerName}</TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span>{supplier.email}</span>
                        <span className="text-sm text-muted-foreground">{supplier.mobile}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center">
                        <MapPin className="h-4 w-4 mr-1 text-muted-foreground" />
                        {supplier.address?.city}, {supplier.address?.state}
                      </div>
                    </TableCell>
                    <TableCell>{getProductNames(supplier)}</TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <StatusBadge status={supplier.isActive ? 'active' : 'inactive'} />
                        <Switch
                          checked={supplier.isActive}
                          onCheckedChange={() => onToggleActive(supplier._id)}
                          onClick={(e) => e.stopPropagation()}
                          disabled={isToggling === supplier._id}
                          aria-label={`Toggle ${supplier.businessName} active status`}
                        />
                      </div>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={isVerified(supplier) ? 'verified' : 'pending'} />
                    </TableCell>
                    <TableCell>
                      <div className="flex space-x-1 justify-end">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            onAddProduct(supplier);
                          }}
                          aria-label={`Add product for ${supplier.businessName}`}
                          title="Add Product"
                        >
                          <Plus className="h-4 w-4 text-green-600" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEdit(supplier);
                          }}
                          aria-label={`Edit ${supplier.businessName}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDelete(supplier._id);
                          }}
                          disabled={isDeleting === supplier._id}
                          aria-label={`Delete ${supplier.businessName}`}
                        >
                          {isDeleting === supplier._id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4 text-red-500" />
                          )}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </ResponsiveTableContainer>
      </div>

      {/* Mobile Card View - visible only on mobile */}
      <div className="block md:hidden h-full flex flex-col">
        <div className="flex-1 overflow-y-auto">
          <div className="space-y-4 p-1">
            {isLoading ? (
              // Loading skeleton cards
              Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="border rounded-lg p-4 space-y-4">
                  <div className="flex justify-between items-start">
                    <div className="space-y-2 flex-1">
                      <Skeleton className="h-5 w-48" />
                      <Skeleton className="h-4 w-32" />
                    </div>
                    <Skeleton className="h-6 w-20" />
                  </div>
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-4 w-2/3" />
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t">
                    <div className="flex items-center space-x-2">
                      <Skeleton className="h-6 w-16" />
                      <Skeleton className="h-4 w-8" />
                    </div>
                    <div className="flex space-x-1">
                      <Skeleton className="h-8 w-8" />
                      <Skeleton className="h-8 w-8" />
                    </div>
                  </div>
                </div>
              ))
            ) : suppliers.length === 0 ? (
              <div className="text-center py-12">
                <div className="flex flex-col items-center space-y-4">
                  <Users className="h-16 w-16 text-muted-foreground" />
                  <div className="space-y-2">
                    <p className="text-xl font-medium">No suppliers found</p>
                    <p className="text-sm text-muted-foreground">Add your first supplier to get started</p>
                  </div>
                </div>
              </div>
            ) : (
              suppliers.map((supplier) => (
                <MobileSupplierCard
                  key={supplier._id}
                  supplier={supplier}
                  onView={onView}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onToggleActive={onToggleActive}
                  onAddProduct={onAddProduct}
                  isDeleting={isDeleting === supplier._id}
                  isToggling={isToggling === supplier._id}
                />
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

ResponsiveSupplierList.displayName = "ResponsiveSupplierList";

export { ResponsiveSupplierList };